import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { useDeviceFingerprint } from "@/lib/useDeviceFingerprint";
import { Toaster } from "sonner";
import { initDeepLinks, hideSplashWhenReady, registerPushNotifications, isNative, configureStatusBar, configureKeyboard } from "@/lib/native";
import { installVersionCheck } from "@/lib/version-check";

// Cache-buster: fire-and-forget on first import. Reloads the WebView
// the moment a fresh deploy is detected so users never have to clear
// app cache after we push a new build.
installVersionCheck();

import Landing from "@/pages/Landing";
import ShareCards from "@/pages/ShareCards";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Verify from "@/pages/Verify";
import Browse from "@/pages/Browse";
import ProductDetail from "@/pages/ProductDetail";
import LiveStream from "@/pages/LiveStream";
import CreateListing from "@/pages/CreateListing";
import Dashboard from "@/pages/Dashboard";
import Admin from "@/pages/Admin";
import AdminRevenue from "@/pages/AdminRevenue";
import AdminCinemaHealth from "@/pages/AdminCinemaHealth";
import AdminPaymentsHealth from "@/pages/AdminPaymentsHealth";
import AdminOrders from "@/pages/AdminOrders";
import AdminScaleHealth from "@/pages/AdminScaleHealth";
import AdminSecurity from "@/pages/AdminSecurity";
import AdminBoost from "@/pages/AdminBoost";
import AdminSupport from "@/pages/AdminSupport";
import Support from "@/pages/Support";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PayPalReturn from "@/pages/PayPalReturn";
import Policy from "@/pages/Policy";
import SellerGuidelines from "@/pages/SellerGuidelines";
import Live from "@/pages/Live";
import FrenzyMoment from "@/pages/FrenzyMoment";
import Categories from "@/pages/Categories";
import Orders from "@/pages/Orders";
import OrderDetail from "@/pages/OrderDetail";
import Payouts from "@/pages/Payouts";
import SellerProfile from "@/pages/SellerProfile";
import ApplyToSell from "@/pages/ApplyToSell";
import WholesaleCredentials from "@/pages/WholesaleCredentials";
import Referrals from "@/pages/Referrals";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Notices from "@/pages/Notices";
import AccountDeletion from "@/pages/AccountDeletion";
import TipSuccess from "@/pages/TipSuccess";
import ProfileEdit from "@/pages/ProfileEdit";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import CollectorProfile from "@/pages/CollectorProfile";
import WhyBidorah from "@/pages/WhyBidorah";
import EmailPreferences from "@/pages/EmailPreferences";
import FollowingFeed from "@/pages/FollowingFeed";
import Plus from "@/pages/Plus";
import PlusSuccess from "@/pages/PlusSuccess";
import Prices from "@/pages/Prices";
import AdminSponsorships from "@/pages/AdminSponsorships";
import LogoLab from "@/pages/LogoLab";
import Brand from "@/pages/Brand";
import ModDashboard from "@/pages/ModDashboard";
import Demo from "@/pages/Demo";
import Messages from "@/pages/Messages";
import SellerHub from "@/pages/SellerHub";
import MobileTabBar from "@/components/MobileTabBar";
import BidoriaBanner from "@/pages/BidoriaBanner";
import ScrollToTop from "@/components/ScrollToTop";
import InstallAppPrompt from "@/components/InstallAppPrompt";
import GetTheAppBanner from "@/components/GetTheAppBanner";
import WhatsNewModal from "@/components/WhatsNewModal";
import FirePreview from "@/pages/FirePreview";
import FounderLetterPage from "@/pages/FounderLetterPage";
import SellerShipping from "@/pages/SellerShipping";
import Recruit from "@/pages/Recruit";
import Leaderboard from "@/pages/Leaderboard";
import HallOfBeacons from "@/pages/HallOfBeacons";
import BeaconCard from "@/pages/BeaconCard";
import AdminBeacon from "@/pages/AdminBeacon";
import { BeaconBoostProvider } from "@/components/beacon/BeaconBoostPill";
import RecruitInvite from "@/pages/RecruitInvite";
import NotificationSettings from "@/pages/NotificationSettings";
import SplashIntro from "@/components/SplashIntro";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  // While auth is resolving, render NOTHING so the native splash screen
  // (or the previous page) stays visible — no jarring "BIDORAH / LOADING…"
  // flash between the splash and the destination route.
  if (loading) return null;
  if (!user) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  return children;
}

// HomeRouter decides what the user sees at "/" — Bidorah's first
// stop after the splash screen. Unauthenticated visitors land on
// /login (per Noel's request); signed-in users land on /dashboard.
// The marketing/landing page is reachable at /landing if needed.
// While auth is still loading we render NOTHING so the SplashIntro
// overlay shows through cleanly — without this branch the user sees
// a brief "BIDORAH / LOADING…" flash between splash and login.
function HomeRouter() {
  const { user, loading } = useAuth();
  if (loading) return null;
  return <Navigate to={user ? "/dashboard" : "/login"} replace />;
}

function AppRouter() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  React.useEffect(() => {
    // Hook native deep links into React Router so tapping a
    // https://bidorah.com/listing/123 link from email/SMS opens the in-app route.
    initDeepLinks(navigate);
    // Fade out the native splash once React has painted.
    hideSplashWhenReady();
    // Configure the Android keyboard so the WebView resizes when the
    // soft keyboard opens — otherwise the chat textarea sits hidden
    // behind the keyboard and sellers see "no keyboard".
    configureKeyboard();
  }, [navigate]);

  // Re-apply the native status bar config on EVERY route change. Some
  // pages (e.g. the LiveStream takeover, login redirects, modal-driven
  // sheets) can mutate the system bar — re-running this guarantees the
  // dark-grey backdrop + white icons are the last thing applied and
  // therefore the visible state. Permanent fix against any future
  // component that touches StatusBar.
  React.useEffect(() => {
    configureStatusBar();
    // Defensive scroll-lock cleanup. Radix DropdownMenu / Dialog
    // primitives can leave `pointer-events: none` or `overflow: hidden`
    // on <body> when an item triggers a route change mid-cleanup. That
    // leaves the next page un-scrollable on mobile until the user
    // navigates away again. Clearing both styles on every route change
    // is cheap and catches any future component that has the same bug.
    if (typeof document !== "undefined") {
      document.body.style.pointerEvents = "";
      document.body.style.overflow = "";
    }
  }, [location.pathname]);

  // Also re-apply on visibility change (returning to the app from background)
  // and on window focus — Android sometimes resets the system bar when the
  // app is recovered from the background or after a notification interaction.
  React.useEffect(() => {
    const reapply = () => configureStatusBar();
    document.addEventListener("visibilitychange", reapply);
    window.addEventListener("focus", reapply);
    return () => {
      document.removeEventListener("visibilitychange", reapply);
      window.removeEventListener("focus", reapply);
    };
  }, []);

  React.useEffect(() => {
    // Register for native push once the user is authenticated.
    if (user && isNative()) {
      registerPushNotifications();
    }
  }, [user]);

  // Compute + upload device fingerprint once after login so the admin
  // security cockpit has a stable device id for this session.
  const deviceFp = useDeviceFingerprint();
  React.useEffect(() => {
    if (user && deviceFp) {
      api.post("/me/device-signal", { fingerprint: deviceFp, app_kind: isNative() ? "native" : "web" }).catch(() => {});
    }
  }, [user, deviceFp]);

  return (
    <>
      <ScrollToTop />
      <Routes>
      <Route path="/" element={<HomeRouter />} />
      <Route path="/landing" element={<Landing />} />
      <Route path="/share-cards" element={<ShareCards />} />
      <Route path="/share-cards/:slug" element={<ShareCards />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/google" element={<AuthCallback />} />
      <Route path="/auth/apple" element={<Login />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      <Route path="/browse" element={<Browse />} />
      <Route path="/categories" element={<Categories />} />
      <Route path="/live" element={<Live />} />
      <Route path="/moment/:id" element={<FrenzyMoment />} />
      <Route path="/policy" element={<Policy />} />
      <Route path="/seller-guidelines" element={<SellerGuidelines />} />
      <Route path="/community-guidelines" element={<SellerGuidelines />} />
      <Route path="/apply-to-sell" element={<ApplyToSell />} />
      <Route path="/referrals" element={<Referrals />} />
      <Route path="/tips/success" element={<TipSuccess />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/notices" element={<Notices />} />
      <Route path="/account-deletion" element={<AccountDeletion />} />
      <Route path="/delete-account" element={<AccountDeletion />} />
      <Route path="/seller/:id" element={<SellerProfile />} />
      <Route path="/collector/:user_id" element={<CollectorProfile />} />
      <Route path="/why-bidorah" element={<WhyBidorah />} />
      <Route path="/demo" element={<Demo />} />
      <Route path="/fire-preview" element={<FirePreview />} />
      <Route path="/letter" element={<FounderLetterPage />} />
      <Route path="/founder-letter" element={<FounderLetterPage />} />
      <Route path="/seller/shipping" element={<SellerShipping />} />
      <Route path="/refer" element={<Recruit />} />
      <Route path="/leaderboard" element={<Leaderboard />} />
      <Route path="/hall-of-beacons" element={<HallOfBeacons />} />
      <Route path="/beacon-card/:user_id" element={<BeaconCard />} />
      <Route
        path="/admin/beacon"
        element={
          <ProtectedRoute>
            <AdminBeacon />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/boost"
        element={
          <ProtectedRoute>
            <AdminBoost />
          </ProtectedRoute>
        }
      />
      <Route path="/r/:code" element={<RecruitInvite />} />
      <Route path="/settings/notifications" element={<NotificationSettings />} />
      <Route path="/og/bidoria" element={<BidoriaBanner />} />
      <Route path="/email/preferences/:token" element={<EmailPreferences />} />
      <Route path="/plus" element={<Plus />} />
      <Route path="/plus/success" element={<PlusSuccess />} />
      <Route path="/prices" element={<Prices />} />
      <Route path="/logo-lab" element={<LogoLab />} />
      <Route path="/brand" element={<Brand />} />
      <Route path="/listing/:id" element={<ProductDetail />} />
      <Route path="/stream/:id" element={<LiveStream />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/paypal/return" element={<PayPalReturn />} />
      <Route
        path="/verify"
        element={
          <ProtectedRoute>
            <Verify />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sell/new"
        element={
          <ProtectedRoute>
            <CreateListing />
          </ProtectedRoute>
        }
      />
      <Route
        path="/seller-hub"
        element={
          <ProtectedRoute>
            <SellerHub />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <Admin />
          </ProtectedRoute>
        }
      />
      <Route
        path="/mod"
        element={
          <ProtectedRoute>
            <ModDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/revenue"
        element={
          <ProtectedRoute>
            <AdminRevenue />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/cinema/health"
        element={
          <ProtectedRoute>
            <AdminCinemaHealth />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/payments/health"
        element={
          <ProtectedRoute>
            <AdminPaymentsHealth />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/orders"
        element={
          <ProtectedRoute>
            <AdminOrders />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/scale-health"
        element={
          <ProtectedRoute>
            <AdminScaleHealth />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/security"
        element={
          <ProtectedRoute>
            <AdminSecurity />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/support"
        element={
          <ProtectedRoute>
            <AdminSupport />
          </ProtectedRoute>
        }
      />
      <Route path="/support" element={<Support />} />
      <Route
        path="/orders"
        element={
          <ProtectedRoute>
            <Orders />
          </ProtectedRoute>
        }
      />
      <Route
        path="/orders/:id"
        element={
          <ProtectedRoute>
            <OrderDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payouts"
        element={
          <ProtectedRoute>
            <Payouts />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wholesale-credentials"
        element={
          <ProtectedRoute>
            <WholesaleCredentials />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings/profile"
        element={
          <ProtectedRoute>
            <ProfileEdit />
          </ProtectedRoute>
        }
      />
      <Route
        path="/feed"
        element={
          <ProtectedRoute>
            <FollowingFeed />
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages"
        element={
          <ProtectedRoute>
            <Messages />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/sponsorships"
        element={
          <ProtectedRoute>
            <AdminSponsorships />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

function App() {
  // Clear the inline backgrounds we injected into `public/index.html`
  // for the pre-React "no white flash" splash. Without this, the
  // `style="background-color:#1D78AB"` on <html> and <body> persists
  // and overrides the `--bidora-bg` (#FAFAFA) CSS rule — every page
  // ends up rendering on the brand-teal pre-splash colour, which
  // ghosts low-contrast text (e.g. the Support hero paragraphs that
  // use text-zinc-600).
  React.useEffect(() => {
    try {
      document.documentElement.style.backgroundColor = "";
      document.body.style.backgroundColor = "";
      // The boot-splash <div> still sits in the DOM right after the
      // root element — remove it the moment React takes over so it
      // can't peek through any transparent surfaces.
      const boot = document.getElementById("boot-splash");
      if (boot && boot.parentNode) boot.parentNode.removeChild(boot);
    } catch (_) { /* defensive — ignore SSR / missing-element cases */ }
  }, []);

  return (
    <div className="App font-body">
      <BrowserRouter>
        <AuthProvider>
          <BeaconBoostProvider>
            <AppRouter />
            <MobileTabBar />
            <SplashIntro />
            <Toaster position="top-right" richColors />
            <RouteAwareChromeShell />
          </BeaconBoostProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

/**
 * Hides the "Get the app" banner, the PWA install prompt, and the
 * "What's new" modal whenever the user is inside a live show
 * (`/stream/*`).  The live page is meant to feel like live sale platforms — true
 * full-bleed video with zero browser-style chrome above or below.
 *
 * On the native Capacitor APK we also hide the Android system status
 * bar + navigation bar so the WebView gets the entire display.
 */
function RouteAwareChromeShell() {
  const location = useLocation();
  const onStream = /^\/stream\//.test(location.pathname);

  React.useEffect(() => {
    // Toggle the body class that drives the global light-grey status-bar
    // strip in index.css.  Strip is ON everywhere except live streams
    // (where the user wants pure full-bleed video to the top edge).
    document.body.classList.toggle("stream-fullbleed", onStream);
    return () => { document.body.classList.remove("stream-fullbleed"); };
  }, [onStream]);

  React.useEffect(() => {
    // Native immersive mode on /stream/* — auto-restored on leave.
    if (!isNative()) return undefined;
    let restored = false;
    let cachedStatusBar = null;
    let cachedStyle = null;

    const applyStyle = async () => {
      try {
        if (!cachedStatusBar) {
          const mod = await import("@capacitor/status-bar");
          cachedStatusBar = mod.StatusBar;
          cachedStyle = mod.Style;
        }
        const SB = cachedStatusBar;
        const Style = cachedStyle;
        // ⚡ AGGRESSIVE VISIBILITY MODE ⚡
        // Setting overlaysWebView=false tells Android the WebView is NOT
        // allowed to draw under the status bar. The OS reserves the top
        // strip for the system bar with a solid background colour, which
        // FORCES the bar to be visible — bypasses the splashImmersive
        // TRANSIENT-bars trap entirely. This costs us the edge-to-edge
        // video bleed, but a visible clock/battery beats no clock/battery.
        try { await SB.setOverlaysWebView({ overlay: false }); } catch { /* noop */ }
        // ⚠️ Capacitor's Style enum is named after the BACKGROUND it's
        // intended for, NOT the icon color. The mapping is:
        //   Style.Dark  = "use on dark backgrounds" → LIGHT/WHITE icons
        //   Style.Light = "use on light backgrounds" → DARK/BLACK icons
        if (onStream) {
          // Live stream: solid black bar at top, white icons.
          try { await SB.setBackgroundColor({ color: "#000000" }); } catch { /* noop */ }
          try { await SB.setStyle({ style: Style?.Dark || "DARK" }); } catch { /* noop */ }
        } else {
          // Rest of the app: light strip, black icons.
          try { await SB.setBackgroundColor({ color: "#E5E7EB" }); } catch { /* noop */ }
          try { await SB.setStyle({ style: Style?.Light || "LIGHT" }); } catch { /* noop */ }
        }
        try { await SB.show(); } catch { /* noop */ }
      } catch { /* plugin missing on web */ }
    };

    // Initial apply.
    applyStyle();

    // JS-only workaround for "splashImmersive flag stranded the status bar
    // hidden" bug. Every 1.5s we re-apply the FULL style (show + bg + style)
    // — each call resets Android's transient auto-hide timer AND re-asserts
    // white icons on stream pages, beating MainActivity.forceShowSystemBars
    // which otherwise calls setAppearanceLightStatusBars(true) at 0/100/500/
    // 1500ms after every page load and forces icons back to DARK. Cheap,
    // runs on the JS event loop, completely imperceptible. No APK rebuild
    // needed.
    const showInterval = setInterval(() => {
      applyStyle();
    }, 1500);

    // Re-show on user interaction — touches/swipes are what Android uses
    // to hide TRANSIENT bars in the first place, so we counter them.
    const handleInteraction = () => {
      if (cachedStatusBar) {
        cachedStatusBar.show().catch(() => { /* ignore */ });
      }
    };
    const handleVisibility = () => {
      if (document.visibilityState === "visible") applyStyle();
    };
    document.addEventListener("touchstart", handleInteraction, { passive: true });
    document.addEventListener("touchend", handleInteraction, { passive: true });
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      clearInterval(showInterval);
      document.removeEventListener("touchstart", handleInteraction);
      document.removeEventListener("touchend", handleInteraction);
      document.removeEventListener("visibilitychange", handleVisibility);
      if (restored) return;
      restored = true;
      (async () => {
        try {
          const { StatusBar, Style } = await import("@capacitor/status-bar");
          await StatusBar.show();
          // Off-stream default: bar always visible, no overlay, light strip.
          try { await StatusBar.setOverlaysWebView({ overlay: false }); } catch { /* noop */ }
          try { await StatusBar.setBackgroundColor({ color: "#E5E7EB" }); } catch { /* noop */ }
          try { await StatusBar.setStyle({ style: Style?.Light || "LIGHT" }); } catch { /* noop */ }
        } catch { /* noop */ }
      })();
    };
  }, [onStream]);

  if (onStream) return null;
  return (
    <>
      <InstallAppPrompt />
      <GetTheAppBanner />
      <WhatsNewModal />
    </>
  );
}

export default App;
